import React, { useRef, useState, useEffect, useCallback } from "react";

import { Typography } from "@mui/material";
import { useMapColorScheme } from "@rubi/ui";
import {
  AdvancedMarker,
  Map,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ICoordinates } from "@/components/online-reporting/types";
import { transformPlaceObject } from "@/components/online-reporting/utils";
import { useServiceConfigurations } from "@/hooks/useCommunities";

const MapSearch: React.FC<any> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  policeService,
  showMap = true,
  disabled = false,
  restrictTo,
}) => {
  const mapColorScheme = useMapColorScheme();
  const { t } = useTranslation("customQuestions");
  const tm = (k: string, o?: Record<string, unknown>) =>
    t(`mapWithSearch.${k}`, o);

  const { orgId } = useParams();
  const serviceConfigurations = useServiceConfigurations();
  const police = policeService
    ? policeService
    : orgId
      ? Object.values(serviceConfigurations).find(
          (config) => config.id === orgId || config.testID === orgId,
        )
      : serviceConfigurations["Timmins Police Service"];

  const [sessionToken, setSessionToken] = useState<any>(null);

  const [location, setLocation] = useState<ICoordinates>(
    values?.googleMapsPlace?.geometry
      ? values?.googleMapsPlace?.geometry
      : police?.mapCenter,
  );
  const [zoom, setZoom] = useState(values?.googleMapsPlace ? 16 : 12);
  const [showUnit, setShowUnit] = useState(
    values?.googleMapsPlace?.address_components &&
      !values?.googleMapsPlace?.address_components?.some((component: any) =>
        component.types.includes("subpremise"),
      ),
  );
  const [placeAutocomplete, setPlaceAutocomplete] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || sessionToken) return;

    setSessionToken(new places.AutocompleteSessionToken());
  }, [places, sessionToken]);

  // Component to handle map updates
  const MapController = () => {
    const map = useMap();

    useEffect(() => {
      if (map && location) {
        map.setCenter(location);
        map.setZoom(zoom);
      }
    }, [map, location, zoom]);

    return null;
  };

  const [addressText, setAddressText] = useState(
    values?.googleMapsPlace?.formatted_address
      ? values?.googleMapsPlace.formatted_address?.includes(
          values?.googleMapsPlace.name ? values.googleMapsPlace.name : "",
        )
        ? values?.googleMapsPlace.formatted_address
        : values?.googleMapsPlace.name +
          " - " +
          values?.googleMapsPlace.formatted_address
      : "",
  );

  const mapOptions = {
    streetViewControl: false,
    tilt: 0,
    fullscreenControl: false,
    rotateControl: false,
  };

  const updatePlacesDetails = useCallback(
    (place: any) => {
      const transformedPlace = transformPlaceObject(place);

      if (!transformedPlace.geometry) {
        return;
      }

      const addressValue = place.formatted_address?.includes(place.name || "")
        ? place.formatted_address
        : place.name + " - " + place.formatted_address;

      setAddressText(addressValue);
      setFieldValue("googleMapsPlace", transformedPlace);

      setShowUnit(
        !place.address_components?.some((component) =>
          component.types.includes("subpremise"),
        ),
      );
      setLocation(transformedPlace.geometry);
      setZoom(16);
    },
    [setFieldValue],
  );

  // Initialize Autocomplete
  useEffect(() => {
    if (
      !places ||
      !sessionToken ||
      !inputRef.current ||
      !(inputRef.current instanceof HTMLInputElement)
    ) {
      return;
    }
    const options: any = {
      fields: [
        "geometry",
        "name",
        "formatted_address",
        "place_id",
        "types",
        "url",
        "address_components",
      ],
      // Focus search results around the police center location
      ...(police?.mapCenter && {
        location: {
          lat: police.mapCenter.lat,
          lng: police.mapCenter.lng,
        },
        radius: 50000, // 50km radius to prioritize results near the police center
        bounds: {
          east: police.mapCenter.lng + 0.05,
          west: police.mapCenter.lng - 0.05,
          north: police.mapCenter.lat + 0.05,
          south: police.mapCenter.lat - 0.05,
        },
      }),
      sessionToken: sessionToken,
    };

    const autocompleteInstance = new places.Autocomplete(
      inputRef.current,
      options,
    );
    setPlaceAutocomplete(autocompleteInstance);

    return () => {};
  }, [places, police, sessionToken]);

  // Handle place selection
  useEffect(() => {
    if (!placeAutocomplete || !places) return;

    const handlePlaceChanged = () => {
      const place = placeAutocomplete.getPlace();
      if (!place.geometry?.location) {
        // If geometry is not available, try to get details
        if (place.place_id) {
          const service = new places.PlacesService(
            document.createElement("div"),
          );
          service.getDetails(
            { placeId: place.place_id },
            (placeResult, status) => {
              if (status === places.PlacesServiceStatus.OK && placeResult) {
                updatePlacesDetails(placeResult);
              }
            },
          );
        }
        return;
      }
      updatePlacesDetails(place);
    };

    const listener = placeAutocomplete.addListener(
      "place_changed",
      handlePlaceChanged,
    );

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, [placeAutocomplete, updatePlacesDetails, places]);

  // Update input value when place changes
  useEffect(() => {
    const place = values?.googleMapsPlace;

    if (!place) {
      // Value cleared externally (e.g. SurveyJS clearValue) — drop derived state
      setAddressText("");
      if (inputRef.current) inputRef.current.value = "";
      setShowUnit(false);
      setLocation(police?.mapCenter);
      setZoom(12);
      return;
    }

    if (place.formatted_address && inputRef.current) {
      const addressValue = place.formatted_address.includes(place.name || "")
        ? place.formatted_address
        : place.name + " - " + place.formatted_address;
      setAddressText(addressValue);
      inputRef.current.value = addressValue;
    }
  }, [values?.googleMapsPlace, police?.mapCenter]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && inputRef.current) {
      event.preventDefault();
      inputRef.current.blur();
      inputRef.current.focus();
    }
  };

  return (
    <>
      {places ? (
        <div style={{ width: "100%" }}>
          {touched.googleMapsPlace && errors.googleMapsPlace && (
            <Typography sx={{ color: "red" }}>
              {errors.googleMapsPlace}
            </Typography>
          )}

          <input
            ref={inputRef}
            value={addressText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAddressText(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            type="text"
            name="googleMapsPlace"
            placeholder={tm("searchAddress")}
            onBlur={handleBlur}
            disabled={disabled}
            autoComplete="off"
            className={`sd-input sd-text ${touched.googleMapsPlace && errors.googleMapsPlace ? "sd-input--error" : ""}`}
          />

          {showUnit && (values?.unit || !disabled) && (
            <div style={{ marginTop: "1rem" }}>
              <h5 className="sd-title sd-element__title sd-question__title sd-question__title--answer">
                <span className="sv-string-viewer sv-string-viewer--multiline">
                  {tm("unitApt")}
                </span>
              </h5>
              <input
                type="text"
                className="sd-input sd-text"
                placeholder={tm("ifApplicable")}
                value={values?.unit ?? ""}
                onChange={(e) => {
                  setFieldValue("unit", e.target.value);
                }}
              />
            </div>
          )}

          {showMap && (
            <Map
              colorScheme={mapColorScheme}
              defaultCenter={location}
              defaultZoom={zoom}
              style={{ height: "412px", width: "100%" }}
              mapTypeId="roadmap"
              streetViewControl={mapOptions.streetViewControl}
              fullscreenControl={mapOptions.fullscreenControl}
              rotateControl={mapOptions.rotateControl}
              tilt={mapOptions.tilt}
            >
              <MapController />
              <AdvancedMarker position={location}>
                <Pin />
              </AdvancedMarker>
            </Map>
          )}
        </div>
      ) : (
        <div>{tm("mapLoading")}</div>
      )}
    </>
  );
};

export default MapSearch;
